import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../Components/PageHeader';
import ProgressionCharts from '../Components/ProgressionCharts';
import { progressionService } from '../services/progressionService';
import { periodeService } from '../services/periodeService';
import { classeService } from '../services/eleveService';
import './StudentProgression.css';

/**
 * Page de progression pour un élève spécifique
 * Affiche le détail de progression par matière, chapitre et leçon
 */
const StudentProgression = () => {
  const { eleveId } = useParams();
  const [progression, setProgression] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMatiere, setSelectedMatiere] = useState(null);
  const [expandedChapitres, setExpandedChapitres] = useState({});

  // Nouveaux états pour la gestion des périodes
  const [periodes, setPeriodes] = useState([]);
  const [selectedPeriode, setSelectedPeriode] = useState(null); // Pas de valeur par défaut
  const [loadingPeriodes, setLoadingPeriodes] = useState(false);

  // États pour la gestion des classes
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(false);

  // États pour les filtres
  const [filters, setFilters] = useState({
    search: '',
    status: '', // 'termine', 'en_cours', null
    showCompleted: true,
    showInProgress: true,
    showNotStarted: true
  });

  // Données d'exemple basées sur votre JSON
  const getExampleData = useCallback(() => ({
    matieres: [
      {
        id: 4,
        slug: "ieTgewb30z",
        id_matiere_de_la_classe: 4,
        id_matiere: 5,
        id_matiere_slug: "OfUwHzUe44",
        titre: "Français",
        statut: "en_cours",
        nombre_chapitres_termines: 0,
        nombre_chapitres_en_cours: 1,
        nombre_chapitres_total: 5,
        nombre_lecons_terminees: 1,
        nombre_lecons_en_cours: 1,
        nombre_lecons_total: 21,
        nombre_cours_termines: 2,
        nombre_cours_en_cours: 0,
        nombre_cours_total: 21,
        nombre_exercices_termines: 2,
        nombre_exercices_en_cours: 0,
        nombre_exercices_total: 42,
        chapitres: [
          {
            id: 1,
            slug: "kQRKhyzPxR",
            titre: "GRAMMAIRE",
            statut: "en_cours",
            nombre_lecons_terminees: 1,
            nombre_lecons_en_cours: 1,
            nombre_lecons_total: 6,
            nombre_cours_termines: 2,
            nombre_cours_en_cours: 0,
            nombre_cours_total: 6,
            nombre_exercices_termines: 2,
            nombre_exercices_en_cours: 0,
            nombre_exercices_total: 12,
            lecons: [
              {
                id: 1,
                slug: "tinj5EGUkd",
                titre: "Leçon 1 : Les éléments dans une situation de communication",
                statut: "termine",
                nombre_cours_termines: 1,
                nombre_cours_en_cours: 0,
                nombre_cours_total: 1,
                nombre_exercices_termines: 2,
                nombre_exercices_en_cours: 0,
                nombre_exercices_total: 2,
                cours: {
                  id: 1,
                  slug: "5flrBdlaOl",
                  titre: "Leçon 1 : Les éléments dans une situation de communication",
                  statut: "termine"
                },
                evaluations: [
                  {
                    id: 800,
                    slug: "FnYEtKs9pr",
                    titre: "Exercice 1",
                    statut: "termine",
                    note: null
                  },
                  {
                    id: 801,
                    slug: "34gceaKEDq",
                    titre: "Exercice 2",
                    statut: "termine",
                    note: null
                  }
                ]
              },
              {
                id: 2,
                slug: "nmtJDEDnJr",
                titre: "Leçon 2 : La langue et la communication : les signes.",
                statut: "en_cours",
                nombre_cours_termines: 1,
                nombre_cours_en_cours: 0,
                nombre_cours_total: 1,
                nombre_exercices_termines: 0,
                nombre_exercices_en_cours: 0,
                nombre_exercices_total: 2,
                cours: {
                  id: 2,
                  slug: "Xmn1OAuyXC",
                  titre: "Leçon 2 : La langue et la communication : les signes.",
                  statut: "termine"
                },
                evaluations: [
                  {
                    id: 802,
                    slug: "CagEj6QpH0",
                    titre: "Exercice 1",
                    statut: null,
                    note: null
                  },
                  {
                    id: 803,
                    slug: "XrcnVP6b6U",
                    titre: "Exercice 2",
                    statut: null,
                    note: null
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        id: 19,
        slug: "CAeUdLMAMo",
        id_matiere_de_la_classe: 19,
        id_matiere: 11,
        id_matiere_slug: "lUNlltTwrz",
        titre: "Education Physique et Sportive",
        statut: "termine",
        nombre_chapitres_termines: 1,
        nombre_chapitres_en_cours: 0,
        nombre_chapitres_total: 1,
        nombre_lecons_terminees: 1,
        nombre_lecons_en_cours: 0,
        nombre_lecons_total: 1,
        nombre_cours_termines: 1,
        nombre_cours_en_cours: 0,
        nombre_cours_total: 1,
        nombre_exercices_termines: 2,
        nombre_exercices_en_cours: 0,
        nombre_exercices_total: 2,
        chapitres: [
          {
            id: 209,
            slug: "YUdeZfbla8",
            titre: "Jeux collectifs",
            statut: "termine",
            nombre_lecons_terminees: 1,
            nombre_lecons_en_cours: 0,
            nombre_lecons_total: 1,
            nombre_cours_termines: 1,
            nombre_cours_en_cours: 0,
            nombre_cours_total: 1,
            nombre_exercices_termines: 2,
            nombre_exercices_en_cours: 0,
            nombre_exercices_total: 2,
            lecons: [
              {
                id: 653,
                slug: "cLb0BnHBvI",
                titre: "Jeux de ballons",
                statut: "termine",
                nombre_cours_termines: 1,
                nombre_cours_en_cours: 0,
                nombre_cours_total: 1,
                nombre_exercices_termines: 2,
                nombre_exercices_en_cours: 0,
                nombre_exercices_total: 2,
                cours: {
                  id: 653,
                  slug: "QoEbIipQ5S",
                  titre: "Jeux de ballons",
                  statut: "termine"
                },
                evaluations: [
                  {
                    id: 1123,
                    slug: "TrXreVUIbi",
                    titre: "Exemple test",
                    statut: "termine",
                    note: null
                  },
                  {
                    id: 1124,
                    slug: "jOeaM1tyaG",
                    titre: "Exercice 2",
                    statut: "termine",
                    note: null
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }), []);



  // Charger les données de progression
  const loadProgression = useCallback(async () => {
    if (!selectedPeriode || !selectedClasse) {
      console.log('Chargement progression annulé - Période:', selectedPeriode, 'Classe:', selectedClasse);
      return;
    }
    
    console.log('Chargement progression pour:', { eleveId, selectedPeriode, selectedClasse });
    setLoading(true);
    try {
      // Appel API réel avec la période et la classe sélectionnées
      const res = await progressionService.getProgressionByEleve(eleveId, selectedPeriode, selectedClasse);
      console.log('Réponse progression:', res);
      setProgression(res.data);
    } catch (error) { 
      console.error('Erreur lors du chargement de la progression:', error);
      toast.error('Erreur lors du chargement de la progression');
      // Utiliser les données d'exemple en cas d'erreur
      setProgression(getExampleData());
    } finally {
      setLoading(false);
    }
  }, [eleveId, selectedPeriode, selectedClasse, getExampleData]);

  // Charger les périodes et classes au montage du composant
  useEffect(() => {
    const initializeData = async () => {
      // Charger les périodes
      setLoadingPeriodes(true);
      try {
        const periodesResponse = await periodeService.getAll();
        console.log('Réponse périodes:', periodesResponse);
        if (periodesResponse && periodesResponse.data) {
          const periodesData = periodesResponse.data.data || periodesResponse.data;
          setPeriodes(periodesData);
          // Sélectionner automatiquement la première période active ou la première disponible
          if (periodesData.length > 0) {
            const defaultPeriode = periodesData.find(p => p.is_active) || periodesData[0];
            setSelectedPeriode(defaultPeriode.id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des périodes:', error);
        toast.error('Erreur lors du chargement des périodes');
        // Utiliser des données de test en cas d'erreur
        const periodesTest = [
          { id: 1, nom: 'Premier Trimestre', label: 'Premier Trimestre', is_active: true },
          { id: 2, nom: 'Deuxième Trimestre', label: 'Deuxième Trimestre', is_active: false },
          { id: 3, nom: 'Troisième Trimestre', label: 'Troisième Trimestre', is_active: false }
        ];
        setPeriodes(periodesTest);
        setSelectedPeriode(1);
      } finally {
        setLoadingPeriodes(false);
      }

      // Charger les classes
      setLoadingClasses(true);
      try {
        const classesResponse = await classeService.getAll();
        console.log('Réponse classes:', classesResponse);
        if (classesResponse && classesResponse.data) {
          const classesData = classesResponse.data.data;
          setClasses(classesData);
          // Sélectionner automatiquement la première classe disponible
          if (classesData.length > 0) {
            setSelectedClasse(classesData[0].id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des classes:', error);
        toast.error('Erreur lors du chargement des classes');
        // Utiliser des données de test en cas d'erreur
        const classesTest = [
          { id: 1, label: 'Classe de 6ème A', description: 'Première classe du collège' },
          { id: 2, label: 'Classe de 5ème B', description: 'Classe de cinquième' },
          { id: 3, label: 'Classe de 4ème C', description: 'Classe de quatrième' }
        ];
        setClasses(classesTest);
        setSelectedClasse(1);
      } finally {
        setLoadingClasses(false);
      }
    };

    initializeData();
  }, []); // Exécuter seulement au montage du composant

  // Charger les données quand l'élève, la période ou la classe change
  useEffect(() => {
    if (eleveId && selectedPeriode && selectedClasse) {
      loadProgression();
    }
  }, [eleveId, selectedPeriode, selectedClasse, loadProgression]);

  // Calculer le pourcentage de progression pour une matière
  const calculateProgressionPercentage = (matiere) => {
    if (matiere.nombre_cours_total === 0 && matiere.nombre_exercices_total === 0) {
      return 0;
    }
    const totalItems = matiere.nombre_cours_total + matiere.nombre_exercices_total;
    const completedItems = matiere.nombre_cours_termines + matiere.nombre_exercices_termines;
    return Math.round((completedItems / totalItems) * 100);
  };

  // Calculer le pourcentage de progression pour un chapitre
  const calculateChapterProgress = (chapitre) => {
    if (chapitre.nombre_cours_total === 0 && chapitre.nombre_exercices_total === 0) {
      return 0;
    }
    const totalItems = chapitre.nombre_cours_total + chapitre.nombre_exercices_total;
    const completedItems = chapitre.nombre_cours_termines + chapitre.nombre_exercices_termines;
    return Math.round((completedItems / totalItems) * 100);
  };

  // Calculer le pourcentage de progression pour une leçon
  const calculateLessonProgress = (lecon) => {
    if (lecon.nombre_cours_total === 0 && lecon.nombre_exercices_total === 0) {
      return 0;
    }
    const totalItems = lecon.nombre_cours_total + lecon.nombre_exercices_total;
    const completedItems = lecon.nombre_cours_termines + lecon.nombre_exercices_termines;
    return Math.round((completedItems / totalItems) * 100);
  };

  // Obtenir la classe CSS pour le statut
  const getStatusClass = (statut) => {
    switch (statut) {
      case 'termine':
        return 'success';
      case 'en_cours':
        return 'warning';
      default:
        return 'light';
    }
  };

  // Obtenir le texte du statut
  const getStatusText = (statut) => {
    switch (statut) {
      case 'termine':
        return 'Terminé';
      case 'en_cours':
        return 'En cours';
      default:
        return 'Non commencé';
    }
  };

  // Filtrer les matières selon les critères
  const getFilteredMatieres = () => {
    if (!progression?.matieres) return [];

    return progression.matieres.filter(matiere => {
      // Filtre par recherche
      if (filters.search && !matiere.titre.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }

      // Filtre par statut
      if (filters.status && matiere.statut !== filters.status) {
        return false;
      }

      // Filtre par type de progression
      if (!filters.showCompleted && matiere.statut === 'termine') return false;
      if (!filters.showInProgress && matiere.statut === 'en_cours') return false;
      if (!filters.showNotStarted && !matiere.statut) return false;

      return true;
    });
  };

  // Toggle expansion d'un chapitre
  const toggleChapterExpansion = (chapitreId) => {
    setExpandedChapitres(prev => ({
      ...prev,
      [chapitreId]: !prev[chapitreId]
    }));
  };

  // Gérer le changement de période
  const handlePeriodeChange = (periodeId) => {
    setSelectedPeriode(parseInt(periodeId));
    // Réinitialiser l'état d'expansion des chapitres
    setExpandedChapitres({});
    setSelectedMatiere(null);
  };

  // Gérer le changement de classe
  const handleClasseChange = (classeId) => {
    setSelectedClasse(parseInt(classeId));
    // Réinitialiser l'état d'expansion des chapitres
    setExpandedChapitres({});
    setSelectedMatiere(null);
  };

  // Calculer les statistiques globales
  const getGlobalStats = () => {
    if (!progression?.matieres) return { total: 0, completed: 0, inProgress: 0 };

    const stats = progression.matieres.reduce((acc, matiere) => {
      acc.total += 1;
      if (matiere.statut === 'termine') acc.completed += 1;
      else if (matiere.statut === 'en_cours') acc.inProgress += 1;
      return acc;
    }, { total: 0, completed: 0, inProgress: 0 });

    return stats;
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <PageHeader title="Progression de l'élève" subTitle="Chargement..." />
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!progression) {
    return (
      <div className="container-fluid">
        <PageHeader title="Progression de l'élève" subTitle="Aucune donnée disponible" />
        <div className="alert alert-warning">
          <h4>Aucune progression trouvée</h4>
          <p>Aucune donnée de progression n'a été trouvée pour cet élève.</p>
        </div>
      </div>
    );
  }

  const filteredMatieres = getFilteredMatieres();
  const globalStats = getGlobalStats();
  const selectedPeriodeInfo = periodes.find(p => p.id === selectedPeriode);
  const selectedClasseInfo = classes.find(c => c.id === selectedClasse);

  return (
    <div className="container-fluid">
      <PageHeader 
        title="Progression de l'élève" 
        subTitle={`Suivi détaillé par matière • ID: ${eleveId}${selectedPeriodeInfo ? ` • ${selectedPeriodeInfo.nom}` : ''}${selectedClasseInfo ? ` • ${selectedClasseInfo.label}` : ''}`}
      />

      {/* Sélecteurs de période et classe */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <label className="form-label fw-bold">
                    <i className="bi bi-calendar3 me-2"></i>
                    Période scolaire
                  </label>
                  <select
                    className="form-select"
                    value={selectedPeriode || ''}
                    onChange={(e) => handlePeriodeChange(e.target.value)}
                    disabled={loadingPeriodes}
                  >
                    {loadingPeriodes ? (
                      <option>Chargement des périodes...</option>
                    ) : periodes.length === 0 ? (
                      <option>Aucune période disponible</option>
                    ) : (
                      <>
                        <option value="">Sélectionner une période</option>
                        {periodes.map(periode => (
                          <option key={periode.id} value={periode.id}>
                            {periode.label || periode.nom} 
                            {periode.is_active && ' (Actuelle)'}
                            {periode.date_debut && periode.date_fin && 
                              ` (${new Date(periode.date_debut).toLocaleDateString()} - ${new Date(periode.date_fin).toLocaleDateString()})`
                            }
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">
                    <i className="bi bi-collection me-2"></i>
                    Classe
                  </label>
                  <select
                    className="form-select"
                    value={selectedClasse || ''}
                    onChange={(e) => handleClasseChange(e.target.value)}
                    disabled={loadingClasses}
                  >
                    {loadingClasses ? (
                      <option>Chargement des classes...</option>
                    ) : classes.length === 0 ? (
                      <option>Aucune classe disponible</option>
                    ) : (
                      <>
                        <option value="">Sélectionner une classe</option>
                        {classes?.map(classe => (
                          <option key={classe.id} value={classe.id}>
                            {classe.label}
                            {classe.description && ` - ${classe.description}`}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <div className="col-md-6">
                  {(selectedPeriodeInfo || selectedClasseInfo) && (
                    <div className="alert alert-info mb-0 py-2">
                      <i className="bi bi-info-circle me-2"></i>
                      {selectedPeriodeInfo && (
                        <>
                          <strong>Période:</strong> {selectedPeriodeInfo.nom}
                          {selectedPeriodeInfo.is_active && (
                            <span className="badge bg-success ms-2">Actuelle</span>
                          )}
                        </>
                      )}
                      {selectedPeriodeInfo && selectedClasseInfo && ' • '}
                      {selectedClasseInfo && (
                        <>
                          <strong>Classe:</strong> {selectedClasseInfo.label}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message si aucune période ou classe sélectionnée */}
      {(!selectedPeriode || !selectedClasse) && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-warning">
              <h4>
                <i className="bi bi-exclamation-triangle me-2"></i>
                Sélection requise
              </h4>
              <p className="mb-0">
                Veuillez sélectionner une période scolaire et une classe pour afficher la progression de l'élève.
              </p>
            </div>
          </div>
        </div>
      )}

      {selectedPeriode && selectedClasse && (
        <>
          {/* Statistiques globales */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-primary mb-2">
                <i className="bi bi-book-fill fs-1"></i>
              </div>
              <h3 className="card-title text-primary">{globalStats.total}</h3>
              <p className="card-text text-muted mb-0">Matières totales</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-success mb-2">
                <i className="bi bi-check-circle-fill fs-1"></i>
              </div>
              <h3 className="card-title text-success">{globalStats.completed}</h3>
              <p className="card-text text-muted mb-0">Matières terminées</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-warning mb-2">
                <i className="bi bi-clock-fill fs-1"></i>
              </div>
              <h3 className="card-title text-warning">{globalStats.inProgress}</h3>
              <p className="card-text text-muted mb-0">Matières en cours</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="text-info mb-2">
                <i className="bi bi-graph-up fs-1"></i>
              </div>
              <h3 className="card-title text-info">
                {globalStats.total > 0 ? Math.round((globalStats.completed / globalStats.total) * 100) : 0}%
              </h3>
              <p className="card-text text-muted mb-0">Progression globale</p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques de progression */}
      <ProgressionCharts progression={progression} />

      {/* Filtres */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Rechercher une matière</label>
              <input
                type="text"
                className="form-control"
                placeholder="Nom de la matière..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Filtrer par statut</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Tous les statuts</option>
                <option value="termine">Terminé</option>
                <option value="en_cours">En cours</option>
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label">Afficher</label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showCompleted"
                    checked={filters.showCompleted}
                    onChange={(e) => setFilters(prev => ({ ...prev, showCompleted: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="showCompleted">
                    Terminés
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showInProgress"
                    checked={filters.showInProgress}
                    onChange={(e) => setFilters(prev => ({ ...prev, showInProgress: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="showInProgress">
                    En cours
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showNotStarted"
                    checked={filters.showNotStarted}
                    onChange={(e) => setFilters(prev => ({ ...prev, showNotStarted: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="showNotStarted">
                    Non commencés
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des matières */}
      <div className="row">
        {filteredMatieres.length === 0 ? (
          <div className="col-12">
            <div className="alert alert-info">
              <h4>Aucune matière trouvée</h4>
              <p>Aucune matière ne correspond aux critères de filtrage sélectionnés.</p>
            </div>
          </div>
        ) : (
          filteredMatieres.map(matiere => {
            const progressionPercentage = calculateProgressionPercentage(matiere);
            
            return (
              <div key={matiere.id} className="col-12 mb-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light border-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="card-title mb-1">
                          <i className="bi bi-book me-2"></i>
                          {matiere.titre}
                        </h5>
                        <div className="d-flex align-items-center gap-3">
                          <span className={`badge bg-${getStatusClass(matiere.statut)}`}>
                            {getStatusText(matiere.statut)}
                          </span>
                          <small className="text-muted">
                            {matiere.nombre_chapitres_total} chapitres • 
                            {matiere.nombre_lecons_total} leçons • 
                            {matiere.nombre_cours_total} cours • 
                            {matiere.nombre_exercices_total} exercices
                          </small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fs-4 fw-bold text-primary">{progressionPercentage}%</div>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSelectedMatiere(selectedMatiere === matiere.id ? null : matiere.id)}
                        >
                          {selectedMatiere === matiere.id ? 'Masquer' : 'Détails'}
                        </button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="progress" style={{ height: '8px' }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${progressionPercentage}%` }}
                          aria-valuenow={progressionPercentage}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                    </div>
                  </div>

                  {selectedMatiere === matiere.id && (
                    <div className="card-body">
                      {/* Statistiques de la matière */}
                      <div className="row mb-4">
                        <div className="col-md-3">
                          <div className="text-center p-3 bg-light rounded">
                            <div className="fs-5 fw-bold text-info">{matiere.nombre_cours_termines}/{matiere.nombre_cours_total}</div>
                            <small className="text-muted">Cours terminés</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-center p-3 bg-light rounded">
                            <div className="fs-5 fw-bold text-success">{matiere.nombre_exercices_termines}/{matiere.nombre_exercices_total}</div>
                            <small className="text-muted">Exercices terminés</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-center p-3 bg-light rounded">
                            <div className="fs-5 fw-bold text-warning">{matiere.nombre_lecons_terminees}/{matiere.nombre_lecons_total}</div>
                            <small className="text-muted">Leçons terminées</small>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-center p-3 bg-light rounded">
                            <div className="fs-5 fw-bold text-primary">{matiere.nombre_chapitres_termines}/{matiere.nombre_chapitres_total}</div>
                            <small className="text-muted">Chapitres terminés</small>
                          </div>
                        </div>
                      </div>

                      {/* Chapitres */}
                      <div className="accordion" id={`accordion-${matiere.id}`}>
                        {matiere.chapitres?.map(chapitre => {
                          const chapterProgress = calculateChapterProgress(chapitre);
                          const isExpanded = expandedChapitres[chapitre.id];

                          return (
                            <div key={chapitre.id} className="accordion-item border-0 mb-2">
                              <h6 className="accordion-header">
                                <button
                                  className="accordion-button collapsed bg-light border-0 rounded"
                                  type="button"
                                  onClick={() => toggleChapterExpansion(chapitre.id)}
                                >
                                  <div className="d-flex justify-content-between w-100 me-3">
                                    <div>
                                      <strong>{chapitre.titre}</strong>
                                      <div className="mt-1">
                                        <span className={`badge bg-${getStatusClass(chapitre.statut)} me-2`}>
                                          {getStatusText(chapitre.statut)}
                                        </span>
                                        <small className="text-muted">
                                          {chapitre.nombre_lecons_total} leçons
                                        </small>
                                      </div>
                                    </div>
                                    <div className="text-end">
                                      <div className="fw-bold text-primary">{chapterProgress}%</div>
                                      <div className="progress mt-1" style={{ width: '100px', height: '4px' }}>
                                        <div
                                          className="progress-bar"
                                          style={{ width: `${chapterProgress}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              </h6>
                              {isExpanded && (
                                <div className="accordion-body">
                                  {chapitre.lecons?.map(lecon => {
                                    const lessonProgress = calculateLessonProgress(lecon);
                                    
                                    return (
                                      <div key={lecon.id} className="card border-0 bg-light mb-3">
                                        <div className="card-body">
                                          <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                              <h6 className="card-title">{lecon.titre}</h6>
                                              <div className="d-flex align-items-center gap-2 mb-2">
                                                <span className={`badge bg-${getStatusClass(lecon.statut)}`}>
                                                  {getStatusText(lecon.statut)}
                                                </span>
                                                <small className="text-muted">
                                                  Cours: {lecon.nombre_cours_termines}/{lecon.nombre_cours_total} • 
                                                  Exercices: {lecon.nombre_exercices_termines}/{lecon.nombre_exercices_total}
                                                </small>
                                              </div>
                                              <div className="progress mb-2" style={{ height: '6px' }}>
                                                <div
                                                  className="progress-bar"
                                                  style={{ width: `${lessonProgress}%` }}
                                                ></div>
                                              </div>
                                              
                                              {/* Détails du cours */}
                                              {lecon.cours && (
                                                <div className="mb-2">
                                                  <div className="d-flex align-items-center">
                                                    <i className="bi bi-play-circle me-2 text-primary"></i>
                                                    <span className="me-2">Cours:</span>
                                                    <span className={`badge bg-${getStatusClass(lecon.cours.statut)}`}>
                                                      {getStatusText(lecon.cours.statut)}
                                                    </span>
                                                  </div>
                                                </div>
                                              )}

                                              {/* Évaluations */}
                                              {lecon.evaluations && lecon.evaluations.length > 0 && (
                                                <div>
                                                  <small className="text-muted d-block mb-2">
                                                    <i className="bi bi-clipboard-check me-1"></i>
                                                    Évaluations:
                                                  </small>
                                                  <div className="d-flex flex-wrap gap-1">
                                                    {lecon.evaluations.map(evaluation => (
                                                      <span
                                                        key={evaluation.id}
                                                        className={`badge bg-${getStatusClass(evaluation.statut)} small`}
                                                        title={evaluation.titre}
                                                      >
                                                        {evaluation.titre}
                                                        {evaluation.note && ` (${evaluation.note})`}
                                                      </span>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                            <div className="text-end ms-3">
                                              <div className="fs-6 fw-bold text-primary">{lessonProgress}%</div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default StudentProgression;
